import { Component, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { StandardSetType } from './settings/standard-set-settings.constants';
import { GraphqlService } from 'src/app/services/graphql/graphql.service';
import { ScopeSequenceStandard } from 'src/app/services/graphql/scope-sequence-standards-response';
import { NewRelicInstrumentationService } from 'src/app/services/new-relic-instrumentation/new-relic-instrumentation.service';
import { DomainStandard } from './models/domain-standards.model';
import { ScopeSequencePendoEventPayload } from './models/scope-sequence-pendo-event.model';
import { PendoService } from 'src/app/services/pendo/pendo.service';
import { ScopeSequenceService } from 'src/app/services/scope-sequence/scope-sequence.service';

@Component({
  selector: 'app-scope-sequence',
  templateUrl: './scope-sequence.component.html',
  styleUrls: ['./scope-sequence.component.scss']
})
export class ScopeSequenceComponent implements OnInit {
  public showTryAgainButton: boolean = false;
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isLoadingStandardResults$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public scopeAndSequenceStandards: ScopeSequenceStandard[] = [];
  public selectedGrade: string = '';
  public selectedSkillOrder: string = '';
  public selectedSubject: string = 'Math';
  public domainStandards: DomainStandard[] = [];

  constructor(
    private graphqlService: GraphqlService,
    private newRelicInstrumentationService: NewRelicInstrumentationService,
    private pendoService: PendoService,
    private scopeSequenceService: ScopeSequenceService
  ) { }

  public async ngOnInit(): Promise<void> {
    this.isLoading$.next(true);
    this.scopeAndSequenceStandards = (await this.graphqlService.getScopeSequenceStandards(this.selectedSubject));
    this.isLoading$.next(false);
  }

  public get isEnvision(): boolean {
    return this.selectedSkillOrder.toLowerCase().includes(StandardSetType.Envision.toLowerCase());
  }

  public async updateSkillOrderSelection(value: string): Promise<void> {
    this.selectedSkillOrder = value;

    if (this.selectedGrade !== '' && this.selectedSubject !== '') {
      this.isLoadingStandardResults$.next(true);
      await this.getFilteredStandards();
      this.isLoadingStandardResults$.next(false);
    }
  }

  public async updateGradeSelection(value: string): Promise<void> {
    this.selectedGrade = value;

    if (this.selectedSkillOrder !== '' && this.selectedSubject !== '') {
      this.isLoadingStandardResults$.next(true);
      await this.getFilteredStandards();
      this.isLoadingStandardResults$.next(false);
    }
  }

  public async updateSubjectSelection(value: string): Promise<void> {
    this.selectedSubject = value;

    if (this.selectedSkillOrder !== '' && this.selectedGrade !== '') {
      this.isLoadingStandardResults$.next(true);
      await this.getFilteredStandards();
      this.isLoadingStandardResults$.next(false);
    }
  }

  private async getFilteredStandards(): Promise<void> {
    this.showTryAgainButton = false;
    const grade = this.selectedGrade;
    const skillOrderValue = this.selectedSkillOrder;
    const subject = this.selectedSubject;

    try {
      const selectedStandardsResponse = await this.graphqlService.getScopeSequenceStandardsGradeStandardSetId(subject, grade, skillOrderValue);
      if (!selectedStandardsResponse || selectedStandardsResponse.length === 0) {
        this.handleStandardSetErrorResponse(new Error('No results for Scope & Sequence'));
        return;
      }

      let standards = structuredClone(selectedStandardsResponse);
      this.domainStandards = this.scopeSequenceService.generateDomainStandards(standards);

      this.sendPendoEvent();
    }
    catch (error) {
      this.handleStandardSetErrorResponse(error as Error);
    }
  }

  private handleStandardSetErrorResponse(error: Error): void {
    this.showTryAgainButton = true;
    this.newRelicInstrumentationService.noticeError(error);
  }

  private sendPendoEvent(): void {
    const selectedFilters = {
      'Subject': this.selectedSubject,
      'Grade': this.selectedGrade,
      'Skill Order': this.selectedSkillOrder,
    };

    const results = this.domainStandards.map(domainStandard => ({
      domainName: domainStandard.domainName,
      standards: domainStandard.standardSets.map(standard => ({
        standardName: standard.standardName,
        standardId: standard.standardId,
        standardDescription: standard.standardDescription,
        skillNames: standard.skillNames ?? [],
      })),
    }));

    let pendoDetails: ScopeSequencePendoEventPayload = { selectedFilters, results };

    this.pendoService.sendEvent('ScopeSequenceMainPage', pendoDetails);
  }
}
